import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const { FiUsers, FiFileText, FiDollarSign, FiActivity, FiUploadCloud, FiX, FiCheckCircle } = FiIcons;

const stats = [
  { name: 'Total Users', value: '1,250', change: '+12.5%', icon: FiUsers, color: 'bg-blue-500' },
  { name: 'Active Referrals', value: '380', change: '+8.2%', icon: FiFileText, color: 'bg-green-500' },
  { name: 'Revenue', value: '$48,350', change: '+20.1%', icon: FiDollarSign, color: 'bg-yellow-500' },
  { name: 'Platform Activity', value: '85%', change: '-1.5%', icon: FiActivity, color: 'bg-red-500' },
];

const chartData = [
  { name: 'Jan', users: 400, referrals: 240 },
  { name: 'Feb', users: 300, referrals: 139 },
  { name: 'Mar', users: 600, referrals: 480 },
  { name: 'Apr', users: 478, referrals: 390 },
  { name: 'May', users: 589, referrals: 480 },
  { name: 'Jun', users: 639, referrals: 580 },
];

const recentActivities = [
  { id: 1, user: 'Dr. Emily Carter', action: 'created a new dealroom', time: '2 hours ago' },
  { id: 2, user: 'Dr. John Smith', action: 'completed a referral', time: '5 hours ago' },
  { id: 3, user: 'Admin', action: 'updated system settings', time: '1 day ago' },
];

const UploadProviders = () => {
    const [file, setFile] = useState(null);
    const [providers, setProviders] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadSuccess, setUploadSuccess] = useState(false);

    const handleFileChange = (event) => {
        const uploadedFile = event.target.files[0];
        if (uploadedFile && uploadedFile.type === "text/csv") {
            setFile(uploadedFile);
            const reader = new FileReader();
            reader.onload = (e) => {
                const text = e.target.result;
                const rows = text.split('\n').slice(1); // Skip header
                const parsedProviders = rows
                    .map(row => {
                        const [name, specialty, email] = row.split(',');
                        return { name, specialty, email };
                    })
                    .filter(p => p.name && p.specialty && p.email);
                setProviders(parsedProviders);
            };
            reader.readAsText(uploadedFile);
        } else {
            alert("Please upload a valid CSV file.");
        }
    };

    const handleUpload = () => {
        setIsUploading(true);
        // Simulate API call
        setTimeout(() => {
            console.log("Uploaded Providers:", providers);
            setIsUploading(false);
            setUploadSuccess(true);
            setFile(null);
            setProviders([]);
            setTimeout(() => setUploadSuccess(false), 3000);
        }, 1500);
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white p-6 rounded-xl shadow-md border border-gray-200"
        >
            <h3 className="text-xl font-bold text-gray-800 mb-4">Upload Referral Providers</h3>
            <div className="flex items-center justify-center w-full">
                <label htmlFor="dropzone-file" className="flex flex-col items-center justify-center w-full h-48 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                        <SafeIcon icon={FiUploadCloud} className="w-10 h-10 mb-3 text-gray-400" />
                        <p className="mb-2 text-sm text-gray-500"><span className="font-semibold">Click to upload</span> or drag and drop</p>
                        <p className="text-xs text-gray-500">CSV file (MAX. 2MB)</p>
                        {file && <p className="text-xs text-brand-primary mt-2">{file.name}</p>}
                    </div>
                    <input id="dropzone-file" type="file" className="hidden" onChange={handleFileChange} accept=".csv" />
                </label>
            </div>
            {providers.length > 0 && (
                <div className="mt-4">
                    <h4 className="font-semibold text-gray-700 mb-2">Providers to be uploaded:</h4>
                    <div className="max-h-48 overflow-y-auto border rounded-lg p-2 bg-gray-50">
                        <ul className="divide-y divide-gray-200">
                            {providers.map((p, index) => (
                                <li key={index} className="py-2 px-1 text-sm">
                                    <span className="font-medium text-gray-800">{p.name}</span> - <span className="text-gray-600">{p.specialty}</span> - <span className="text-gray-500 italic">{p.email}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="flex justify-end gap-3 mt-4">
                        <button onClick={() => { setFile(null); setProviders([]); }} className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100">Cancel</button>
                        <button onClick={handleUpload} disabled={isUploading} className="px-4 py-2 text-sm font-semibold text-white bg-brand-primary rounded-lg hover:bg-brand-secondary disabled:bg-gray-400">
                            {isUploading ? 'Uploading...' : `Upload ${providers.length} Providers`}
                        </button>
                    </div>
                </div>
            )}
            {uploadSuccess && (
                 <motion.div initial={{opacity: 0, y: 10}} animate={{opacity: 1, y: 0}} className="mt-4 flex items-center gap-2 p-3 rounded-lg bg-green-100 text-green-800">
                    <SafeIcon icon={FiCheckCircle} />
                    <span className="text-sm font-medium">Providers uploaded successfully!</span>
                 </motion.div>
            )}
        </motion.div>
    );
};

const AdminDashboard = () => {
  const { user } = useAuth();

  return (
    <div className="p-6 bg-gray-50 min-h-screen font-sans">
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Admin Dashboard</h1>
        <p className="text-gray-600">Welcome back, {user?.name || 'Admin'}. Here's your platform overview.</p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 my-6">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.name}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            className="bg-white p-6 rounded-xl shadow-md border border-gray-200 flex items-center"
          >
            <div className={`p-3 rounded-full text-white mr-4 ${stat.color}`}>
              <SafeIcon icon={stat.icon} className="h-6 w-6" />
            </div>
            <div>
              <p className="text-sm text-gray-500">{stat.name}</p>
              <div className="flex items-baseline space-x-2">
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                <span className={`text-sm font-semibold ${stat.change.startsWith('+') ? 'text-green-500' : 'text-red-500'}`}>
                  {stat.change}
                </span>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="lg:col-span-2 bg-white p-6 rounded-xl shadow-md border border-gray-200"
        >
          <h3 className="text-xl font-bold text-gray-800 mb-4">Platform Growth</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="name" axisLine={false} tickLine={false} />
              <YAxis axisLine={false} tickLine={false} />
              <Tooltip
                contentStyle={{
                  borderRadius: '0.5rem',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                  border: '1px solid #e5e7eb'
                }}
              />
              <Legend iconType="circle" />
              <Bar dataKey="users" fill="#3b82f6" name="New Users" radius={[4, 4, 0, 0]} />
              <Bar dataKey="referrals" fill="#10b981" name="Completed Referrals" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="bg-white p-6 rounded-xl shadow-md border border-gray-200"
        >
          <h3 className="text-xl font-bold text-gray-800 mb-4">Recent Activity</h3>
          <ul className="space-y-4">
            {recentActivities.map(activity => (
              <li key={activity.id} className="flex items-start">
                <div className="bg-gray-100 rounded-full p-2 mr-3">
                  <SafeIcon icon={FiActivity} className="h-5 w-5 text-gray-500" />
                </div>
                <div>
                  <p className="text-sm text-gray-800">
                    <span className="font-semibold">{activity.user}</span> {activity.action}
                  </p>
                  <p className="text-xs text-gray-400">{activity.time}</p>
                </div>
              </li>
            ))}
          </ul>
        </motion.div>
      </div>

      <div className="mt-6">
        <UploadProviders />
      </div>

    </div>
  );
};

export default AdminDashboard;